const STORAGE_PREFIX = 'amposter:creation-drafts:v1:';
const BINARY_DB = 'amposter-creation-drafts';
const BINARY_STORE = 'files';
export const DRAFTS_CHANGED_EVENT = 'amposter:drafts-changed';

export const DRAFT_TYPE_META = {
  post: { label: 'منشور', route: '/create' },
  poll: { label: 'تصويت', route: '/create-poll' },
  video: { label: 'فيديو', route: '/create?type=video' },
  quiz: { label: 'كويز', route: '/create-quiz' },
  newspaper: { label: 'جريدة', route: '/create?type=newspaper' },
  event: { label: 'فعالية', route: '/create-event' },
};

const storageKey = (userId) => `${STORAGE_PREFIX}${userId || 'guest'}`;
const binaryKey = (userId, draftId, name) => `${userId || 'guest'}:${draftId}:${name}`;

const openBinaryDb = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') return reject(new Error('IndexedDB is unavailable'));
  const request = indexedDB.open(BINARY_DB, 1);
  request.onupgradeneeded = () => request.result.createObjectStore(BINARY_STORE);
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

export const saveDraftBinary = async (userId, draftId, name, value) => {
  try {
    const db = await openBinaryDb();
    await new Promise((resolve, reject) => {
      const request = db.transaction(BINARY_STORE, 'readwrite').objectStore(BINARY_STORE).put(value, binaryKey(userId, draftId, name));
      request.onsuccess = resolve;
      request.onerror = () => reject(request.error);
    });
    db.close();
    return true;
  } catch (error) {
    console.warn('Could not save draft media:', error);
    return false;
  }
};

export const getDraftBinary = async (userId, draftId, name) => {
  if (!draftId) return null;
  try {
    const db = await openBinaryDb();
    const value = await new Promise((resolve, reject) => {
      const request = db.transaction(BINARY_STORE).objectStore(BINARY_STORE).get(binaryKey(userId, draftId, name));
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
    db.close();
    return value;
  } catch (error) {
    console.warn('Could not restore draft media:', error);
    return null;
  }
};

export const removeDraftBinaries = async (userId, draftId) => {
  try {
    const db = await openBinaryDb();
    const prefix = `${userId || 'guest'}:${draftId}:`;
    await new Promise((resolve, reject) => {
      const store = db.transaction(BINARY_STORE, 'readwrite').objectStore(BINARY_STORE);
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return resolve();
        if (String(cursor.key).startsWith(prefix)) cursor.delete();
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
    db.close();
  } catch (error) {
    console.warn('Could not remove draft media:', error);
  }
};

const readAll = (userId) => {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey(userId)) || '[]');
    return Array.isArray(value) ? value : [];
  } catch (error) {
    console.warn('Could not read creation drafts:', error);
    return [];
  }
};

const writeAll = (userId, drafts) => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(drafts));
    window.dispatchEvent(new CustomEvent(DRAFTS_CHANGED_EVENT, { detail: { userId } }));
    return true;
  } catch (error) {
    console.warn('Could not save creation draft:', error);
    return false;
  }
};

export const createDraftId = (type = 'post') =>
  `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const listCreationDrafts = (userId) =>
  readAll(userId).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

export const getCreationDraft = (userId, draftId) =>
  readAll(userId).find((draft) => draft.id === draftId) || null;

export const saveCreationDraft = ({ userId, id, type, title, summary, data }) => {
  if (!type || !DRAFT_TYPE_META[type]) return null;
  const drafts = readAll(userId);
  const existingIndex = drafts.findIndex((draft) => draft.id === id);
  const now = Date.now();
  const draft = {
    ...(existingIndex >= 0 ? drafts[existingIndex] : {}),
    id: id || createDraftId(type),
    type,
    title: String(title || '').trim(),
    summary: String(summary || '').trim(),
    data: data || {},
    createdAt: existingIndex >= 0 ? drafts[existingIndex].createdAt : now,
    updatedAt: now,
  };

  if (existingIndex >= 0) drafts[existingIndex] = draft;
  else drafts.push(draft);

  return writeAll(userId, drafts) ? draft : null;
};

export const removeCreationDraft = (userId, draftId) => {
  return removeCreationDrafts(userId, [draftId]);
};

export const removeCreationDrafts = (userId, draftIds) => {
  const ids = new Set(draftIds);
  if (!writeAll(userId, readAll(userId).filter(draft => !ids.has(draft.id)))) return false;
  ids.forEach(id => { void removeDraftBinaries(userId, id); });
  return true;
};

export const getDraftResumePath = (draft) => {
  const base = DRAFT_TYPE_META[draft?.type]?.route || '/drafts';
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}draft=${encodeURIComponent(draft.id)}`;
};

export const plainTextFromHtml = (value = '') => String(value)
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();
